import React, { useEffect, useState } from 'react';
import { todayISO, money, moneyTight } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Partners from './Partners';
import { api } from '../../utils/api';
import { usePayrollConfig, pnd1Monthly, ssoAmount } from '../ClientOnboard/payroll';
import { TaxView } from '../Tax';
import FileLibrary from '../FileLibrary';
import DocNoLink from '../DocNoLink';
import { brandOr } from '../../utils/brand';

// Branch (สาขา) is a 6-digit code; a shorter entry is left-padded with zeros (e.g. "1234" → "001234", head office "000000").
const pad6 = (b: any) => String(b ?? '').trim().padStart(6, '0');
const fmtDate = (ymd: string) => { const p = (ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (ymd || '—'); };
type Period = 'month' | 'year' | 'all';
function periodRange(p: Period): { from: string; to: string } {
  const now = new Date(todayISO()); const y = now.getFullYear(), m = now.getMonth();
  if (p === 'all') return { from: '', to: '' };
  if (p === 'year') return { from: `${y}-01-01`, to: `${y}-12-31` };
  const mm = String(m + 1).padStart(2, '0'); const lastD = new Date(y, m + 1, 0).getDate();
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(lastD).padStart(2, '0')}` };
}
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 };
const cardTitle: React.CSSProperties = { fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 };
const kvLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.03em' };
// 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were dark grey in bold, 10px above and below where it was 6, on one 16px line, on white, over the same darker
// line — a 37px band, as every list's is.
// 10 → 5px under the heading text (report c151d269: "Bring the heading text 5 pixels closer to the grey line
// below. Do this for every page." — see Active Jobs' th; the 8px sides stay).
const dlTh: React.CSSProperties = { fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 8px 5px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
const dlTd: React.CSSProperties = { fontSize: 12.5, padding: '7px 8px', borderBottom: '1px solid var(--border)' };

function ServiceList({ title, items }: { title: string; items: any[] }) {
  return (
    <div style={card}>
      <div style={cardTitle}>{title} <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>({items.length})</span></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
        {items.map(s => (
          <div key={s.id} style={{ fontSize: 13, borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, flex: 1 }}>{s.name}{s.code && <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}> · {s.code}</span>}</span>
              <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{moneyTight(s.fee)}</span>
            </div>
            {s.description && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.5, whiteSpace: 'normal' }}>{s.description}</div>}
          </div>
        ))}
        {!items.length && <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>—</div>}
      </div>
    </div>
  );
}

// Each number in the Quotation / Invoice / Receipt blue, and a link to its document (Calem, Bugify #b4cccf02 — see
// DocNoLink); a row's id is its document's own. Were the app's green accent.
function BillingBlock({ title, data }: { title: string; data: { rows: any[]; total: number } }) {
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={cardTitle}>{title}</div>
        <div style={{ fontSize: 16, fontWeight: 800 }}>{moneyTight(data.total)}</div>
      </div>
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <tbody>
            {data.rows.map((r: any) => (
              <tr key={r.id}><td style={{ padding: '5px 0', color: 'var(--accent)', fontWeight: 600 }}><DocNoLink no={r.doc_no} id={r.id} /></td><td style={{ color: 'var(--text-tertiary)' }}>{fmtDate(r.issue_date)}</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{moneyTight(r.amount)}</td></tr>
            ))}
            {!data.rows.length && <tr><td colSpan={3} style={{ color: 'var(--text-tertiary)', padding: 8 }}>—</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Reveal flow: ask for the operator's own login password (once per 5-minute window), fetch the
// decrypted values, auto-hide them again after 60s so they cannot sit on a shared screen.
function CredentialsCard({ client, card, cardTitle, kvLabel, t }: any) {
  const [creds, setCreds] = useState<any>(null);
  const [ask, setAsk] = useState(false);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!creds) return;
    const h = setTimeout(() => setCreds(null), 60_000);
    return () => clearTimeout(h);
  }, [creds]);
  const reveal = async (password?: string) => {
    setBusy(true); setErr('');
    try {
      const d: any = await api.clientCredentials(client.id, password);
      setCreds(d); setAsk(false); setPw('');
    } catch (e: any) {
      if (/incorrect/i.test(e.message)) { setAsk(true); setErr(t('cl_pw_wrong')); }
      else if (/Too many/i.test(e.message)) { setAsk(false); setErr(e.message); }
      else if (/401|password/i.test(e.message)) setAsk(true);
      else setErr(e.message);
    } finally { setBusy(false); }
  };
  const row = (label: string, u: string, p: string, has: boolean) => (!u && !has) ? null : (
    <div key={label} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, paddingBottom: 8 }}>
      <div><div style={kvLabel}>{label} · {t('cl_login_user')}</div><div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{u || '—'}</div></div>
      <div><div style={kvLabel}>{label} · {t('cl_login_pass')}</div>
        <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2, fontFamily: p ? 'inherit' : 'monospace', letterSpacing: p ? 0 : 2 }}>
          {p || (has ? '••••••••' : '—')}
        </div>
      </div>
    </div>
  );
  return (
    <div style={card}>
      <div style={{ ...cardTitle, display: 'flex', alignItems: 'center' }}>
        <i className="ti ti-key" />{t('cl_gov_logins')}
        <span style={{ flex: 1 }} />
        {creds
          ? <button onClick={() => setCreds(null)} style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-app)', borderRadius: 7, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}><i className="ti ti-eye-off" /> {t('cl_hide')}</button>
          : <button onClick={() => reveal()} disabled={busy} style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-app)', borderRadius: 7, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}><i className="ti ti-eye" /> {t('cl_reveal')}</button>}
      </div>
      {ask && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 10px', padding: '8px 10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#92400E' }}>{t('cl_reauth')}</span>
          <input type="password" value={pw} autoFocus onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === 'Enter' && reveal(pw)}
            style={{ padding: '5px 9px', fontSize: 12.5, border: '1px solid var(--border-strong)', borderRadius: 7, width: 180 }} />
          <button onClick={() => reveal(pw)} disabled={busy || !pw} style={{ border: 'none', background: 'var(--accent)', color: '#fff', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: busy || !pw ? 0.6 : 1 }}>{t('cl_confirm')}</button>
          <button onClick={() => { setAsk(false); setPw(''); setErr(''); }} style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-app)', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>{t('cancel')}</button>
        </div>
      )}
      {err && <div style={{ fontSize: 11.5, color: '#B42318', marginBottom: 8 }}>{err}</div>}
      {row(t('cl_sso_login'), creds?.sso?.username ?? client?.sso_username, creds?.sso?.password || '', !!client?.has_sso_password)}
      {row(t('cl_dbd_login'), creds?.dbd?.username ?? client?.dbd_username, creds?.dbd?.password || '', !!client?.has_dbd_password)}
      {row(t('cl_rev_login'), creds?.revenue?.username ?? client?.revenue_username, creds?.revenue?.password || '', !!client?.has_revenue_password)}
      {creds && <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{t('cl_reveal_note')}</div>}
    </div>
  );
}

export default function ClientDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const payCfg = usePayrollConfig();
  const [showSso, setShowSso] = useState(false);
  const [params] = useSearchParams();
  const id = params.get('id') || '';
  const [client, setClient] = useState<any>(null);
  const [services, setServices] = useState<{ legal: any[]; accounting: any[] }>({ legal: [], accounting: [] });
  const [people, setPeople] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [empFilter, setEmpFilter] = useState<'active' | 'terminated' | 'all'>('active');
  const [billing, setBilling] = useState<any>(null);
  const [deadlines, setDeadlines] = useState<any[]>([]);
  const [balanceSheet, setBalanceSheet] = useState<any>(null);
  const [period, setPeriod] = useState<Period>('month');
  const [tab, setTab] = useState<'overview' | 'services' | 'tax' | 'billing' | 'tasks' | 'files' | 'balance' | 'partners'>('overview');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!id) return;
    api.clientDashboard(id).then((d: any) => {
      setClient(d.client); setServices(d.services || { legal: [], accounting: [] });
      setDeadlines(d.deadlines || []); setPeople(d.people || []); setEmployees(d.employees || []);
      setBalanceSheet(d.balanceSheet || null);
    }).catch((e: any) => setErr(e.message));
  }, [id]); // eslint-disable-line
  useEffect(() => { if (!id) return; const r = periodRange(period); api.clientBilling(id, r.from, r.to).then(setBilling).catch(() => {}); }, [id, period]); // eslint-disable-line

  const firms: string[] = (() => { try { return JSON.parse(client?.firms || '[]'); } catch { return []; } })();
  const acctStatus: string[] = (() => { try { const a = JSON.parse(client?.acct_status || '[]'); return Array.isArray(a) ? a : []; } catch { return []; } })();
  const isAccounting = firms.includes('accounting');
  // A natural person has no VAT/PND/SSO filings, no statutory balance sheet and no business partners
  // — those belong to a juristic person. Owner 2026-08-12: "个人客户根本就不存在这些".
  const isCompany = (client?.entity_type || 'company') !== 'individual';
  const showCompanyTabs = isAccounting && isCompany;
  // Landing on a tab that has just been hidden (client switched to individual) would show a blank pane.
  useEffect(() => {
    if (!showCompanyTabs && (tab === 'tax' || tab === 'balance' || tab === 'partners')) setTab('overview');
  }, [showCompanyTabs, tab]);
  const title = client?.company_name || client?.client_name || '—';

  const tabBtn = (key: typeof tab, label: string, badge?: string) => (
    <button key={key} onClick={() => setTab(key)} style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', borderBottom: '2px solid', borderColor: tab === key ? 'var(--accent)' : 'transparent', background: 'transparent', color: tab === key ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer' }}>{label}{badge && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-tertiary)' }}>{badge}</span>}</button>
  );
  const periodBtn = (p: Period, label: string) => (
    <button key={p} onClick={() => setPeriod(p)} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid', cursor: 'pointer', borderColor: period === p ? 'var(--accent)' : 'var(--border-strong)', background: period === p ? 'var(--accent)' : 'transparent', color: period === p ? '#fff' : 'var(--text-secondary)' }}>{label}</button>
  );
  const soc = (icon: string, val: string) => val ? <a href={/^https?:/.test(val) ? val : `https://${val}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}><i className={`ti ti-brand-${icon}`} style={{ fontSize: 18 }} /></a> : null;
  const kv = (label: string, value: React.ReactNode) => <div><div style={kvLabel}>{label}</div><div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{value || '—'}</div></div>;
  const bnum = (v: any): string => { if (v === '' || v == null) return ''; const n = Number(v); return Number.isFinite(n) ? money(n) : String(v); };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}><img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} /></button>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
        <div style={{ display: 'flex', gap: 6 }}>{firms.map(f => <span key={f} style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: f === 'legal' ? '#EFF6FF' : '#F0FDF4', color: f === 'legal' ? '#1D4ED8' : '#15803D' }}>{f}</span>)}</div>
      </header>

      {!id ? <div style={{ padding: 40, color: 'var(--text-tertiary)' }}>{t('cd_no_client')}</div> : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', padding: '0 12px', flexWrap: 'wrap' }}>
            {tabBtn('overview', t('cd_overview'))}
            {tabBtn('services', t('cd_services'), (services.legal.length + services.accounting.length) ? String(services.legal.length + services.accounting.length) : undefined)}
            {showCompanyTabs && tabBtn('tax', t('cd_tax'))}
            {showCompanyTabs && tabBtn('balance', t('cd_balance'))}
            {tabBtn('billing', t('cd_billing'))}
            {tabBtn('tasks', t('cd_tasks'), deadlines.length ? String(deadlines.length) : undefined)}
            {showCompanyTabs && tabBtn('partners', t('pt_title'))}
            {tabBtn('files', t('cd_files'))}
          </div>
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 16px' }}>{err}</div>}

          {tab === 'tax' ? (
            <TaxView clientId={id} />
          ) : (
            <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
              {tab === 'overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Basic info — everything the client provided, so staff get the full picture */}
                  <div style={card}>
                    <div style={cardTitle}><i className="ti ti-building" />{t('cd_basic')}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
                      {kv(t('cd_company'), client?.company_name)}
                      {kv(t('cd_contact'), client?.client_name)}
                      {kv(t('cd_type'), client?.client_type)}
                      {kv(t('cd_firms'), firms.join(' · '))}
                      {kv(t('cd_tax_id'), client?.tax_id)}
                      {kv(t('cl_branch'), pad6(client?.branch_no))}
                      {kv(t('cd_vat'), client?.has_vat ? t('cd_yes') : (client?.has_vat === 0 ? t('cd_no') : ''))}
                      {kv('Email', client?.email)}
                      {kv(t('cd_phone'), client?.phone)}
                      {kv(t('cd_founding'), client?.founding_date ? fmtDate(client.founding_date) : '')}
                      {(client?.bank_name || client?.bank_account_no) && <>
                        {kv(t('cl_bank'), client?.bank_name)}
                        {kv(t('cl_bank_branch'), client?.bank_branch)}
                        {kv(t('cl_bank_acc_name'), client?.bank_account_name)}
                        {kv(t('cl_bank_acc_no'), client?.bank_account_no)}
                      </>}
                      {kv(t('sd_birthday'), client?.birthday ? fmtDate(client.birthday) : '')}
                      {kv(t('cd_acct_status'), acctStatus.join(' · '))}
                      {kv(t('cd_monthly_fee'), client?.monthly_fee ? moneyTight(client.monthly_fee) : '')}
                    </div>
                    {client?.registered_address && <div>{kv(t('on_reg_address'), client.registered_address)}</div>}
                    {client?.registered_address_th && <div>{kv(t('on_reg_address_th'), client.registered_address_th)}</div>}
                    {client?.address && <div>{kv(t('cd_address'), client.address)}</div>}
                    {(client?.linkedin || client?.facebook || client?.instagram) && (
                      <div style={{ display: 'flex', gap: 14 }}>{soc('linkedin', client?.linkedin)}{soc('facebook', client?.facebook)}{soc('instagram', client?.instagram)}</div>
                    )}
                  </div>

                  {/* Government e-filing logins. The passwords are encrypted at rest and never travel in
                      the client payload — revealing one asks for the viewer's own password and is audited. */}
                  {(client?.sso_username || client?.has_sso_password || client?.dbd_username || client?.has_dbd_password || client?.revenue_username || client?.has_revenue_password) && (
                    <CredentialsCard client={client} card={card} cardTitle={cardTitle} kvLabel={kvLabel} t={t} />
                  )}

                  {/* Directors (DBD cert) and Shareholders (BOJ.5) — shown as two separate tables. */}
                  {(() => {
                    const directors = people.filter((p: any) => p.role !== 'shareholder');
                    const shareholders = people.filter((p: any) => p.role !== 'director');
                    const idTypeLabel = (k: string) => k === 'thai_id' ? 'Thai ID' : k === 'passport' ? 'Passport' : '';
                    const idCell = (p: any) => p.id_number ? `${idTypeLabel(p.id_type) ? idTypeLabel(p.id_type) + ' ' : ''}${p.id_number}` : '—';
                    const nameCell = (p: any) => <td style={{ ...dlTd, fontWeight: 600 }}>{p.name || '—'}{p.is_foreign ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#B45309' }}>{t('cd_foreign')}</span> : null}</td>;
                    return <>
                      {directors.length > 0 && (
                        <div style={card}>
                          <div style={cardTitle}><i className="ti ti-gavel" />{t('cd_directors')} <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>({directors.length})</span><span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--text-tertiary)' }}>· {t('cd_directors_src')}</span></div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead><tr><th style={dlTh}>{t('cd_name')}</th><th style={dlTh}>{t('cd_nationality')}</th><th style={dlTh}>{t('cd_id')}</th><th style={dlTh}>{t('cd_dob')}</th><th style={dlTh}>{t('cd_signing')}</th></tr></thead>
                              <tbody>
                                {directors.map((p: any) => (
                                  <tr key={p.id}>{nameCell(p)}<td style={dlTd}>{p.nationality || '—'}</td><td style={dlTd}>{idCell(p)}</td><td style={dlTd}>{p.dob ? fmtDate(p.dob) : '—'}</td><td style={{ ...dlTd, color: 'var(--text-tertiary)' }}>{p.signing_authority || '—'}</td></tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {shareholders.length > 0 && (
                        <div style={card}>
                          <div style={cardTitle}><i className="ti ti-chart-pie" />{t('cd_shareholders')} <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>({shareholders.length})</span><span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--text-tertiary)' }}>· {t('cd_shareholders_src')}</span></div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead><tr><th style={dlTh}>{t('cd_name')}</th><th style={dlTh}>{t('cd_nationality')}</th><th style={dlTh}>{t('cd_id')}</th><th style={{ ...dlTh, textAlign: 'right' }}>{t('cd_shareholding')}</th></tr></thead>
                              <tbody>
                                {shareholders.map((p: any) => (
                                  <tr key={p.id}>{nameCell(p)}<td style={dlTd}>{p.nationality || '—'}</td><td style={dlTd}>{idCell(p)}</td><td style={{ ...dlTd, textAlign: 'right' }}>{p.shareholding_pct ? `${p.shareholding_pct}%` : '—'}</td></tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>;
                  })()}

                  {/* Employees (from onboarding). Terminated staff stay on file forever — red rows,
                      hidden behind the Active filter by default (owner 2026-08-08). */}
                  {employees.length > 0 && (() => {
                    const d0 = new Date();
                    const today = `${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, '0')}-${String(d0.getDate()).padStart(2, '0')}`;
                    const isTerm = (e: any) => !!e.termination_date && e.termination_date < today;
                    const shown = employees.filter((e: any) => empFilter === 'all' ? true : (empFilter === 'terminated' ? isTerm(e) : !isTerm(e)));
                    const chip = (k: 'active' | 'terminated' | 'all', label: string) => (
                      <button key={k} onClick={() => setEmpFilter(k)} style={{
                        border: `1px solid ${empFilter === k ? 'var(--c-teal-600, #0d9488)' : 'var(--border)'}`,
                        background: empFilter === k ? 'rgba(13,148,136,.09)' : 'transparent',
                        color: empFilter === k ? 'var(--c-teal-700, #0f766e)' : 'var(--text-secondary)',
                        borderRadius: 999, padding: '2px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{label}</button>
                    );
                    return (
                    <div style={card}>
                      <div style={{ ...cardTitle, flexWrap: 'wrap' }}><i className="ti ti-id-badge-2" />{t('cd_employees')} <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>({shown.length})</span>
                        <span style={{ display: 'inline-flex', gap: 6, marginLeft: 'auto' }}>{chip('active', t('emp_active'))}{chip('terminated', t('emp_terminated'))}{chip('all', t('emp_all'))}</span>
                      </div>
                      <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr>
                            <th style={dlTh}>{t('cd_name')}</th><th style={dlTh}>{t('cd_position')}</th><th style={dlTh}>{t('cd_nationality')}</th>
                            <th style={dlTh}>{t('cd_id')}</th><th style={dlTh}>{t('on_sso_date')}</th><th style={dlTh}>{t('cd_term_date')}</th><th style={{ ...dlTh, textAlign: 'right' }}>{t('cd_salary')}</th>
                            <th style={{ ...dlTh, textAlign: 'right' }}>{t('on_wht_pnd1')}</th><th style={{ ...dlTh, textAlign: 'right' }}>{t('on_sso')}</th>
                          </tr></thead>
                          <tbody>
                            {shown.map((e: any) => {
                              // O2 (batch 10 R2): an employee the server sent without pay (pay_hidden) shows "— No access" in
                              // the three pay cells — never a figure computed from the missing salary.
                              const hid = !!e.pay_hidden;
                              const noAccess = <span data-pay-hidden title={t('ecl_pay_hidden_tip')} style={{ color: 'var(--text-tertiary)' }}>{t('ecl_pay_hidden')}</span>;
                              const wht = e.wht_override != null ? Number(e.wht_override) : (payCfg ? pnd1Monthly(e.monthly_salary, payCfg.sso, payCfg.pnd1) : null);
                              const sso = e.sso_override != null ? Number(e.sso_override) : (payCfg ? ssoAmount(e.monthly_salary, payCfg.sso) : null);
                              const term = isTerm(e);
                              const tdS = term ? { ...dlTd, color: '#d92d20' } : dlTd;
                              return (
                              <tr key={e.id}>
                                <td style={{ ...tdS, fontWeight: 600 }}>{e.name || '—'}{term && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#d92d20', border: '1px solid #fda29b', background: '#fef3f2', borderRadius: 5, padding: '1px 5px', verticalAlign: 'middle' }}>{t('emp_terminated').toUpperCase()}</span>}</td>
                                <td style={tdS}>{e.position || '—'}</td>
                                <td style={tdS}>{e.nationality || '—'}</td>
                                <td style={tdS}>{e.id_number || '—'}</td>
                                <td style={tdS}>{e.sso_status || '—'}</td>
                                <td style={{ ...tdS, fontWeight: e.termination_date ? 700 : 400 }}>{e.termination_date || '—'}</td>
                                <td style={{ ...tdS, textAlign: 'right' }}>{hid ? noAccess : e.monthly_salary ? moneyTight(e.monthly_salary) : '—'}</td>
                                <td style={{ ...tdS, textAlign: 'right' }}>{hid ? noAccess : wht == null ? '…' : moneyTight(wht)}</td>
                                <td style={{ ...tdS, textAlign: 'right' }}>{hid ? noAccess : sso == null ? '…' : moneyTight(sso)}</td>
                              </tr>
                            ); })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    ); })()}
                </div>
              )}

              {tab === 'services' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 14 }}>
                  <ServiceList title={`${t('cd_svc')} · Legal`} items={services.legal} />
                  <ServiceList title={`${t('cd_svc')} · Accounting`} items={services.accounting} />
                </div>
              )}

              {tab === 'balance' && (
                !balanceSheet || !balanceSheet.lines?.length ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 10, color: 'var(--text-tertiary)' }}>
                    <i className="ti ti-file-spreadsheet" style={{ fontSize: 40 }} />
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{t('cd_no_balance')}</div>
                  </div>
                ) : (
                  <div style={card}>
                    <div style={cardTitle}><i className="ti ti-file-spreadsheet" />{t('cd_balance')}
                      <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)' }}>
                        {balanceSheet.as_of ? `${t('cl_bs_as_of')}: ${balanceSheet.as_of}` : ''}{client?.books_up_to ? ` · ${t('cl_books_up_to')}: ${client.books_up_to}` : ''}
                      </span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 760 }}>
                        <thead>
                          <tr>
                            <th rowSpan={2} style={dlTh}>{t('cl_bs_code')}</th>
                            <th rowSpan={2} style={dlTh}>{t('cl_bs_desc')}</th>
                            <th colSpan={2} style={{ ...dlTh, textAlign: 'center' }}>{t('cl_bs_initial')}</th>
                            <th colSpan={2} style={{ ...dlTh, textAlign: 'center' }}>{balanceSheet.period}</th>
                            <th colSpan={2} style={{ ...dlTh, textAlign: 'center' }}>{t('cl_bs_end')}</th>
                          </tr>
                          <tr>
                            {['initDr', 'initCr', 'perDr', 'perCr', 'endDr', 'endCr'].map((k, j) => (
                              <th key={k} style={{ ...dlTh, textAlign: 'right' }}>{j % 2 === 0 ? t('cl_bs_debit') : t('cl_bs_credit')}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {balanceSheet.lines.map((r: any, i: number) => (
                            <tr key={i} style={{ background: r.isTotal ? 'var(--c-gray-50)' : 'transparent', fontWeight: r.isTotal ? 700 : 400 }}>
                              <td style={{ ...dlTd, whiteSpace: 'nowrap' }}>{r.code}</td>
                              <td style={dlTd}>{r.desc}</td>
                              {['initDr', 'initCr', 'perDr', 'perCr', 'endDr', 'endCr'].map(k => (
                                <td key={k} style={{ ...dlTd, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{bnum(r[k])}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              )}

              {tab === 'billing' && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('sd_period')}:</span>
                    {periodBtn('month', t('sd_this_month'))}{periodBtn('year', t('sd_this_year'))}{periodBtn('all', t('sd_all'))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 14 }}>
                    {billing ? <>
                      <BillingBlock title={t('sd_invoiced')} data={billing.invoices} />
                      <BillingBlock title={t('sd_quoted')} data={billing.quotations} />
                      <BillingBlock title={t('sd_receipted')} data={billing.receipts} />
                    </> : <div style={{ color: 'var(--text-tertiary)' }}>…</div>}
                  </div>
                </>
              )}

              {tab === 'tasks' && (
                deadlines.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 10, color: 'var(--text-tertiary)' }}>
                    <i className="ti ti-calendar-repeat" style={{ fontSize: 40 }} />
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{t('cd_no_tasks')}</div>
                  </div>
                ) : (
                  <div style={{ ...card, maxWidth: 720 }}>
                    <div style={cardTitle}><i className="ti ti-calendar-repeat" />{t('cd_tasks')}</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={dlTh}>{t('cmp_due')}</th><th style={dlTh}>{t('cmp_filing')}</th>
                        <th style={dlTh}>{t('cmp_period')}</th><th style={{ ...dlTh, textAlign: 'right' }}>{t('cmp_status')}</th>
                      </tr></thead>
                      <tbody>
                        {deadlines.map((d: any) => {
                          const overdue = d.status === 'pending' && d.due_date < new Date().toISOString().slice(0, 10);
                          const sc = d.status === 'filed' ? { bg: '#ECFDF5', c: '#15803D', l: t('cmp_filed') } : d.status === 'na' ? { bg: 'var(--c-gray-100)', c: 'var(--text-tertiary)', l: 'N/A' } : overdue ? { bg: '#FEF2F2', c: '#DC2626', l: t('cmp_pending') } : { bg: '#FFFBEB', c: '#B45309', l: t('cmp_pending') };
                          return (
                            <tr key={d.id}>
                              <td style={{ ...dlTd, fontWeight: 700, color: overdue ? '#DC2626' : 'var(--text-primary)', whiteSpace: 'nowrap' }}>{fmtDate(d.due_date)}</td>
                              <td style={dlTd}>{d.label || d.kind}</td>
                              <td style={{ ...dlTd, color: 'var(--text-tertiary)' }}>{d.period}</td>
                              <td style={{ ...dlTd, textAlign: 'right' }}><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: sc.bg, color: sc.c }}>{sc.l}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}
              {tab === 'partners' && id && <Partners clientId={id} />}
              {tab === 'files' && id && <FileLibrary scope={`client:${id}`} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
